/**
 * Zumvexa 2.0 — theme behaviour.
 *
 * A vanilla ES module made of small custom elements; no dependencies.
 * Server-rendered HTML stays the single source of truth: anything that
 * changes (cart, carousel pages, quick view, search) is rendered by Shopify
 * via the Section Rendering API or an alternate template and swapped in.
 */

const { routes, strings, cartType } = window.theme;
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/* ---------------------------------------------------------------------------
 * Utilities
 * ------------------------------------------------------------------------ */

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

const debounce = (fn, wait = 250) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

const onFrame = (fn) => {
  let queued = false;
  return () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      fn();
    });
  };
};

/**
 * Parse fetched HTML. DOMParser runs with scripting off, so <noscript>
 * fallbacks (e.g. the variant <select name="id">, the cart "Update" button)
 * would otherwise turn into live elements — strip them.
 */
const parseHTML = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('noscript').forEach((node) => node.remove());
  return doc;
};

const REQUEST_TIMEOUT = 12000;

/**
 * Cart and section requests. Stalled requests are dropped rather than left
 * hanging, and Shopify's rate limit (429) is retried once, honouring
 * Retry-After, so a shopper clicking Add to cart repeatedly sees no error.
 */
async function requestJSON(url, options, attempt = 0) {
  let response;
  try {
    response = await fetch(url, {
      ...options,
      credentials: 'same-origin',
      signal: options?.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT),
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...options?.headers },
    });
  } catch (error) {
    if (error.name === 'TimeoutError' || error.name === 'AbortError') throw new Error(strings.error);
    throw error;
  }

  if ((response.status === 429 || response.status === 503) && attempt < 1) {
    const after = Number(response.headers.get('Retry-After')) || 1;
    await new Promise((resolve) => setTimeout(resolve, Math.min(after, 5) * 1000));
    return requestJSON(url, options, attempt + 1);
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) throw new Error(data.description || data.message || strings.error);
  return data;
}

/* ---------------------------------------------------------------------------
 * Spam trap
 * Public forms carry a hidden field (snippets/honeypot.liquid). People never
 * see it; bots fill everything. A filled trap quietly drops the submit.
 * ------------------------------------------------------------------------ */

document.addEventListener(
  'submit',
  (event) => {
    const trap = event.target.querySelector?.('[data-honeypot]');
    if (trap && trap.value.trim() !== '') {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true
);

/* ---------------------------------------------------------------------------
 * Reveal on scroll
 * Elements already on screen are marked revealed before the hiding CSS is
 * armed (html.reveal-on), so nothing above the fold ever flashes.
 * ------------------------------------------------------------------------ */

const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-revealed');
      revealObserver.unobserve(entry.target);
    }
  },
  { rootMargin: '0px 0px -6% 0px' }
);

function reveal(root = document) {
  const enabled =
    document.body.classList.contains('has-reveal') && !window.theme.designMode && !reducedMotion.matches;
  const armed = document.documentElement.classList.contains('reveal-on');

  root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((element) => {
    if (!enabled || (!armed && element.getBoundingClientRect().top < innerHeight)) {
      element.classList.add('is-revealed');
    } else {
      revealObserver.observe(element);
    }
  });

  if (enabled) document.documentElement.classList.add('reveal-on');
}

/* ---------------------------------------------------------------------------
 * Dialogs — native <dialog> drawers and modals
 * [data-open="DialogId"] opens, [data-close] or a backdrop click closes.
 * ------------------------------------------------------------------------ */

function openDialog(dialog) {
  if (!dialog || dialog.open) return;
  document.querySelectorAll('dialog[open]').forEach((other) => other.close());
  dialog.showModal();
}

let pointerDownTarget = null;
document.addEventListener('pointerdown', (event) => {
  pointerDownTarget = event.target;
});

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.open);
    if (dialog) {
      event.preventDefault();
      openDialog(dialog);
    }
    return;
  }

  if (event.target.closest('[data-close]')) {
    event.target.closest('dialog')?.close();
    return;
  }

  // A click on the backdrop targets the <dialog> itself (its panel fills it).
  const dialog = event.target;
  if (dialog instanceof HTMLDialogElement && dialog.open && pointerDownTarget === dialog) dialog.close();
});

/* ---------------------------------------------------------------------------
 * Sticky header — hides on scroll down, returns on scroll up.
 * Publishes --header-height for scroll padding and sticky columns.
 * ------------------------------------------------------------------------ */

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.section-header') ?? this;
    this.resizeObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--header-height', `${this.section.offsetHeight}px`);
    });
    this.resizeObserver.observe(this.section);

    if (this.dataset.mode !== 'smart') return;
    let lastY = scrollY;
    // Scroll events already fire at most once per frame, so no rAF wrapper —
    // the header must never stay hidden near the top, even in a background tab.
    this.onScroll = () => {
      const y = scrollY;
      if (y <= this.section.offsetHeight + 160) {
        document.documentElement.classList.remove('header-hidden');
        lastY = y;
        return;
      }
      if (Math.abs(y - lastY) < 8) return;
      const busy = document.querySelector('dialog[open]') || this.contains(document.activeElement);
      document.documentElement.classList.toggle('header-hidden', y > lastY && !busy);
      lastY = y;
    };
    addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    removeEventListener('scroll', this.onScroll);
    document.documentElement.classList.remove('header-hidden');
  }
}
define('sticky-header', StickyHeader);

/* ---------------------------------------------------------------------------
 * Announcement bar — messages slide in from the right, or scroll as a
 * continuous ticker. Pauses on hover/focus; static for reduced motion.
 * ------------------------------------------------------------------------ */

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.messages = [...this.querySelectorAll('.announcement__message')];
    this.track = this.querySelector('.announcement__track');
    if (!this.messages.length) return;

    this.addEventListener('pointerenter', () => this.pause(true));
    this.addEventListener('pointerleave', () => this.pause(false));
    this.addEventListener('focusin', () => this.pause(true));
    this.addEventListener('focusout', () => this.pause(false));

    if (this.dataset.motion === 'marquee') {
      this.setupMarquee();
      return;
    }

    if (this.messages.length < 2) return;
    this.index = 0;
    // Browsers freeze animations in hidden tabs; tidy up when the tab comes back.
    this.onVisibility = () => (document.hidden ? this.stop() : (this.reset(), this.start()));
    document.addEventListener('visibilitychange', this.onVisibility);
    this.addEventListener('shopify:block:select', (event) => {
      this.stop();
      this.go(this.messages.indexOf(event.target));
    });
    this.start();
  }

  // Show only the current message, with no half-finished slide left over.
  reset() {
    this.messages.forEach((message, i) => {
      clearTimeout(message.settleTimer);
      message.classList.remove('is-entering', 'is-leaving');
      message.hidden = i !== this.index;
    });
  }

  /* --- Slide: one message at a time, out to the left, next in from the right --- */
  go(index) {
    const next = (index + this.messages.length) % this.messages.length;
    if (next === this.index) return;
    const leaving = this.messages[this.index];
    const entering = this.messages[next];
    this.index = next;

    // The resting state is "visible", so a message can never be left invisible.
    clearTimeout(entering.settleTimer);
    entering.classList.remove('is-leaving');
    entering.classList.add('is-entering');
    entering.hidden = false;
    const land = () => entering.classList.remove('is-entering');
    requestAnimationFrame(() => requestAnimationFrame(land));
    setTimeout(land, 60);

    leaving.classList.add('is-leaving');
    clearTimeout(leaving.settleTimer);
    leaving.settleTimer = setTimeout(() => {
      leaving.classList.remove('is-leaving');
      leaving.hidden = true;
    }, reducedMotion.matches ? 0 : 700);
  }

  start() {
    this.stop();
    if (reducedMotion.matches || window.theme.designMode) return;
    this.timer = setInterval(() => this.go(this.index + 1), Number(this.dataset.interval) || 5000);
  }

  stop() {
    clearInterval(this.timer);
  }

  /* --- Ticker: the messages scroll right to left without stopping --- */
  setupMarquee() {
    if (reducedMotion.matches || window.theme.designMode) return;
    const group = this.track.firstElementChild;
    if (!group) return;

    // Repeat the messages until they are wider than the bar, then clone the
    // whole strip once so it can loop back to the start without a jump.
    let guard = 0;
    while (this.track.scrollWidth < this.clientWidth * 1.5 && guard < 20) {
      this.track.append(this.duplicate(group));
      guard += 1;
    }
    [...this.track.children].forEach((child) => this.track.append(this.duplicate(child)));

    const distance = this.track.scrollWidth / 2;
    const speed = Number(this.dataset.speed) || 60;
    this.track.style.setProperty('--marquee-duration', `${Math.max(distance / speed, 8)}s`);
    this.classList.add('is-scrolling');
  }

  // A visual copy: screen readers and keyboards only use the original.
  duplicate(node) {
    const copy = node.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.querySelectorAll('a').forEach((link) => (link.tabIndex = -1));
    copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    return copy;
  }

  pause(state) {
    this.classList.toggle('is-paused', state);
    if (this.dataset.motion !== 'marquee') (state ? this.stop() : this.start());
  }

  disconnectedCallback() {
    this.stop();
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
  }
}
define('announcement-bar', AnnouncementBar);

/* ---------------------------------------------------------------------------
 * Predictive search
 * ------------------------------------------------------------------------ */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-results]');
    this.cache = new Map();

    this.input.addEventListener('input', debounce(() => this.search(), 220));
    this.input.addEventListener('focus', () => this.open());
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.close();
    });
    this.onOutside = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    document.addEventListener('pointerdown', this.onOutside);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onOutside);
  }

  async search() {
    const terms = this.input.value.trim();
    if (!terms) {
      this.close();
      this.results.replaceChildren();
      return;
    }

    // Shopify matches word prefixes, so a single letter ("d", "b") already returns results.
    const params = new URLSearchParams({
      q: terms,
      section_id: 'predictive-search',
      'resources[type]': 'product,collection,query',
      'resources[limit]': '8',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
    });
    const url = `${routes.predictiveSearch}?${params}`;

    this.controller?.abort();
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');

    try {
      let html = this.cache.get(url);
      if (!html) {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.statusText);
        html = await response.text();
        this.cache.set(url, html);
      }
      const content = parseHTML(html).querySelector('.predictive__content');
      this.results.replaceChildren(...(content ? [content] : []));
      this.open();
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  open() {
    if (!this.results.childElementCount || !this.input.value.trim()) return;
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  onKeydown(event) {
    if (event.key === 'Escape' && !this.results.hidden) {
      event.preventDefault();
      this.close();
      this.input.focus();
      return;
    }
    if ((event.key !== 'ArrowDown' && event.key !== 'ArrowUp') || this.results.hidden) return;

    event.preventDefault();
    const items = [...this.results.querySelectorAll('[data-predictive-item]')];
    const current = items.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
    if (next < 0) this.input.focus();
    else items[Math.min(next, items.length - 1)]?.focus();
  }
}
define('predictive-search', PredictiveSearch);

/* ---------------------------------------------------------------------------
 * Cart — Cart AJAX API + Section Rendering API
 * ------------------------------------------------------------------------ */

function cartSectionIds() {
  const ids = new Set(['cart-drawer']);
  document.querySelectorAll('cart-items[data-sections]').forEach((element) => {
    element.dataset.sections.split(',').forEach((id) => ids.add(id.trim()));
  });
  return [...ids];
}

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((bubble) => {
    const changed = bubble.textContent.trim() !== String(count);
    bubble.textContent = count;
    bubble.hidden = count === 0;
    if (changed) {
      bubble.classList.remove('is-bumped');
      void bubble.offsetWidth;
      bubble.classList.add('is-bumped');
    }
  });
}

function renderCartSections(sections) {
  if (!sections) return;

  // Remember which control had focus so it survives the re-render.
  const active = document.activeElement;
  const activeLine = active?.closest('[data-line]')?.dataset.line;
  const activeStep = active?.dataset.step;
  const activeScope = active?.closest('cart-items')?.dataset.sections;

  for (const [id, html] of Object.entries(sections)) {
    if (!html) continue;
    const doc = parseHTML(html);

    if (id === 'cart-drawer') {
      // Swap only the panel so the <dialog> stays open.
      const fresh = doc.querySelector('[data-cart-panel]');
      const current = document.querySelector('#CartDrawer [data-cart-panel]');
      if (fresh && current) {
        current.replaceWith(fresh);
        updateCartCount(Number(fresh.dataset.cartCountValue));
      }
      continue;
    }

    const target = document.getElementById(`shopify-section-${id}`);
    const fresh = doc.getElementById(`shopify-section-${id}`);
    if (target && fresh) target.innerHTML = fresh.innerHTML;
  }

  if (activeLine && activeScope) {
    const scope = document.querySelector(`cart-items[data-sections="${activeScope}"]`);
    const selector = activeStep ? `[data-step="${activeStep}"]` : '.qty__input';
    const next = scope?.querySelector(`[data-line="${activeLine}"] ${selector}`) ?? scope?.querySelector('.qty__input');
    next?.focus();
  }
}

async function addToCart(formData) {
  formData.append('sections', cartSectionIds().join(','));
  formData.append('sections_url', location.pathname);
  return requestJSON(`${routes.cartAdd}.js`, { method: 'POST', body: formData });
}

async function changeCartLine(line, quantity) {
  return requestJSON(`${routes.cartChange}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ line, quantity, sections: cartSectionIds(), sections_url: location.pathname }),
  });
}

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-step]');
      if (!button) return;
      const min = Number(this.input.min) || 0;
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const current = parseInt(this.input.value, 10) || 0;
      const next = Math.min(max, Math.max(min, current + Number(button.dataset.step)));
      if (next === current) return;
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}
define('quantity-input', QuantityInput);

class CartItems extends HTMLElement {
  connectedCallback() {
    this.timers = new Map();

    this.addEventListener('change', (event) => {
      const input = event.target.closest('.qty__input[data-line]');
      if (!input) return;
      const line = Number(input.dataset.line);
      clearTimeout(this.timers.get(line));
      this.timers.set(
        line,
        setTimeout(() => this.update(line, Math.max(0, parseInt(input.value, 10) || 0)), 350)
      );
    });

    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove]');
      if (!remove) return;
      event.preventDefault();
      this.update(Number(remove.dataset.remove), 0);
    });
  }

  async update(line, quantity) {
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    try {
      const cart = await changeCartLine(line, quantity);
      renderCartSections(cart.sections);
      const item = cart.items[line - 1];
      if (quantity > 0 && item && item.quantity < quantity) {
        this.showError(line, strings.error);
      }
    } catch (error) {
      this.showError(line, error.message);
      const input = this.querySelector(`.qty__input[data-line="${line}"]`);
      if (input) input.value = input.defaultValue;
    } finally {
      // The element may have been replaced by the re-render.
      this.classList.remove('is-loading');
      this.removeAttribute('aria-busy');
    }
  }

  showError(line, message) {
    const target = document.querySelector(
      `cart-items[data-sections="${this.dataset.sections}"] [data-line="${line}"] [data-line-error]`
    );
    if (!target) return;
    target.textContent = message;
    target.hidden = false;
  }
}
define('cart-items', CartItems);

/* ---------------------------------------------------------------------------
 * Product form — AJAX add to cart, then open the drawer
 * ------------------------------------------------------------------------ */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.error = this.querySelector('[data-form-error]');
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  get buttons() {
    // Not `form.id`: the form has an <input name="id">, which shadows that property.
    const id = CSS.escape(this.form.getAttribute('id'));
    return document.querySelectorAll(`[form="${id}"][name="add"], #${id} [name="add"]`);
  }

  async onSubmit(event) {
    if (cartType === 'page') return; // native POST redirects to /cart
    event.preventDefault();
    if (this.form.getAttribute('aria-busy') === 'true') return;

    this.form.setAttribute('aria-busy', 'true');
    this.buttons.forEach((button) => button.classList.add('is-loading'));
    if (this.error) this.error.hidden = true;

    try {
      const data = await addToCart(new FormData(this.form));
      if (document.body.classList.contains('template-cart')) {
        location.assign(routes.cart);
        return;
      }
      renderCartSections(data.sections);
      openDialog(document.getElementById('CartDrawer'));
    } catch (error) {
      if (this.error) {
        this.error.textContent = error.message;
        this.error.hidden = false;
      }
    } finally {
      this.form.removeAttribute('aria-busy');
      this.buttons.forEach((button) => button.classList.remove('is-loading'));
    }
  }
}
define('product-form', ProductForm);

/* ---------------------------------------------------------------------------
 * Variant picker — resolves the chosen options to a variant client-side
 * using the JSON rendered next to it, then announces `variant:change`.
 * ------------------------------------------------------------------------ */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.groups = [...this.querySelectorAll('[data-option]')];
    this.addEventListener('change', () => this.onChange());
    this.updateAvailability();
  }

  get selected() {
    return this.groups.map((group) => {
      if (group.matches('select, input')) return group.value;
      return group.querySelector('input:checked')?.value;
    });
  }

  onChange() {
    const selected = this.selected;
    const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
    this.groups.forEach((group, i) => {
      const label = group.querySelector('[data-selected-value]');
      if (label) label.textContent = selected[i] ?? '';
    });
    this.updateAvailability();
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
  }

  updateAvailability() {
    const selected = this.selected;
    this.groups.forEach((group, index) => {
      const choices = group.matches('select') ? [...group.options] : [...group.querySelectorAll('input[type="radio"]')];
      choices.forEach((choice) => {
        const available = this.variants.some(
          (v) => v.available && v.options[index] === choice.value && v.options.every((o, i) => i === index || o === selected[i])
        );
        choice.toggleAttribute('data-unavailable', !available);
        if (choice.tagName === 'OPTION') {
          choice.textContent = available ? choice.value : `${choice.value} — ${strings.soldOut}`;
        }
      });
    });
  }
}
define('variant-picker', VariantPicker);

/* ---------------------------------------------------------------------------
 * Product info — reacts to variant changes (price, button, URL, gallery)
 * ------------------------------------------------------------------------ */

class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.addEventListener('variant:change', (event) => this.render(event.detail.variant));
  }

  render(variant) {
    const buttons = this.querySelectorAll('[name="add"]');
    const idInput = this.querySelector('form input[name="id"]');

    if (!variant) {
      buttons.forEach((button) => this.setButton(button, false, strings.unavailable));
      return;
    }

    if (idInput) idInput.value = variant.id;

    this.querySelectorAll('[data-price]').forEach((price) => {
      const onSale = Boolean(variant.compare);
      price.classList.toggle('price--sale', onSale);
      const current = price.querySelector('[data-price-current]');
      if (current) current.innerHTML = `<span class="money" data-cents="${variant.price_cents}">${variant.price}</span>`;
      const was = price.querySelector('[data-price-compare]');
      if (was) {
        was.hidden = !onSale;
        const money = was.querySelector('.money');
        if (money && onSale) {
          money.dataset.cents = variant.compare_cents;
          delete money.dataset.moneyOriginal;
          money.textContent = variant.compare;
        }
      }
      const badge = price.querySelector('[data-price-badge]');
      if (badge) {
        badge.hidden = !onSale;
        badge.textContent = `−${variant.save}%`;
      }
    });

    buttons.forEach((button) => this.setButton(button, variant.available, variant.available ? strings.addToCart : strings.soldOut));

    const sku = this.querySelector('[data-sku]');
    if (sku) {
      sku.hidden = !variant.sku;
      sku.querySelector('span').textContent = variant.sku ?? '';
    }

    if (this.dataset.updateUrl === 'true') {
      history.replaceState(history.state, '', `${this.dataset.url}?variant=${variant.id}`);
    }

    window.theme.currencyDisplay?.apply(this);

    if (variant.media) this.querySelector('media-gallery')?.goTo(variant.media);
  }

  setButton(button, enabled, label) {
    button.disabled = !enabled;
    const text = button.querySelector('[data-label]');
    if (text) text.textContent = label;
  }
}
define('product-info', ProductInfo);

/* ---------------------------------------------------------------------------
 * Media gallery — scroll-snap slides, synced thumbnails and counter
 * ------------------------------------------------------------------------ */

class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.counter = this.querySelector('[data-counter]');

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(thumb.dataset.thumb)));
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));

    // Open on the selected variant's photo (e.g. /products/x?variant=123).
    if (this.dataset.initial) requestAnimationFrame(() => this.goTo(this.dataset.initial, false));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  goTo(mediaId, smooth = true) {
    const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'instant' });
  }

  step(direction) {
    const index = Math.min(this.slides.length - 1, Math.max(0, (this.activeIndex ?? 0) + direction));
    this.goTo(this.slides[index].dataset.mediaId);
  }

  setActive(index) {
    if (index < 0 || index === this.activeIndex) return;
    this.activeIndex = index;
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
    this.thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.toggleAttribute('aria-current', active);
      if (active) {
        const rail = thumb.parentElement;
        const vertical = rail.scrollHeight > rail.clientHeight;
        rail.scrollTo({
          top: vertical ? thumb.offsetTop - rail.clientHeight / 2 + thumb.offsetHeight / 2 : 0,
          left: vertical ? 0 : thumb.offsetLeft - rail.clientWidth / 2 + thumb.offsetWidth / 2,
          behavior: 'smooth',
        });
      }
    });
  }
}
define('media-gallery', MediaGallery);

/* ---------------------------------------------------------------------------
 * Carousel — native scroll-snap track with arrows, mouse drag, autoplay
 * and infinite loading from the `collection.cards` alternate template.
 * ------------------------------------------------------------------------ */

class CarouselSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    this.nextUrl = this.dataset.nextUrl || null;

    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.counter = this.querySelector('[data-slide-current]');
    this.progressBar = this.querySelector('[data-autoplay-progress]');
    this.activeIndex = 0;

    this.prevButton?.addEventListener('click', () => this.page(-1));
    this.nextButton?.addEventListener('click', () => this.page(1));
    this.dots.forEach((dot, index) =>
      dot.addEventListener('click', () => {
        this.track.scrollTo({ left: index * this.itemWidth, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      })
    );
    this.track.addEventListener('scroll', (this.onScroll = onFrame(() => this.update())), { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);

    this.enableDrag();
    this.setupAutoplay();
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.visibility?.disconnect();
    cancelAnimationFrame(this.autoplayFrame);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** Called when a hidden tab panel becomes visible. */
  activate() {
    if (this.nextUrl && !this.track.querySelector('[data-carousel-item]')) this.loadMore();
    this.update();
  }

  get itemWidth() {
    const item = this.track.querySelector('[data-carousel-item]');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return item ? item.offsetWidth + gap : this.track.clientWidth;
  }

  page(direction) {
    const perPage = Math.max(1, Math.floor((this.track.clientWidth + 1) / this.itemWidth));
    this.scrollBy(direction * perPage * this.itemWidth);
  }

  scrollBy(left) {
    this.track.scrollBy({ left, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    if (!clientWidth) return;
    const max = scrollWidth - clientWidth;
    const start = Math.min(1, clientWidth / scrollWidth);

    this.toggleAttribute('data-overflowing', max > 4 || Boolean(this.nextUrl));
    if (this.prevButton) this.prevButton.disabled = scrollLeft <= 4;
    if (this.nextButton) this.nextButton.disabled = scrollLeft >= max - 4 && !this.nextUrl;
    this.style.setProperty('--progress-start', start.toFixed(3));
    this.style.setProperty('--progress', (max > 0 ? start + (1 - start) * (scrollLeft / max) : 1).toFixed(3));

    if (this.dots.length) {
      const active = Math.round(scrollLeft / this.itemWidth);
      this.dots.forEach((dot, i) => {
        dot.toggleAttribute('aria-current', i === active);
        dot.toggleAttribute('data-seen', i < active);
      });
      if (active !== this.activeIndex) {
        this.activeIndex = active;
        this.autoplayElapsed = 0; // a new slide always gets its full time
      }
      if (this.counter) this.counter.textContent = String(active + 1).padStart(2, '0');
    }

    this.warmImages();
    if (this.nextUrl && scrollLeft >= max - clientWidth * 1.5) this.loadMore();
  }

  /**
   * Browsers only start lazy images inside a horizontal scroller once they are
   * visible, which flashes empty cards mid-swipe. Promote the photos of the
   * next two screens' worth of items so they are ready before they arrive.
   */
  warmImages() {
    const items = this.track.querySelectorAll('[data-carousel-item]');
    if (!items.length) return;
    const width = this.itemWidth;
    const first = Math.floor(this.track.scrollLeft / width);
    const perView = Math.ceil(this.track.clientWidth / width);
    for (let i = first; i < Math.min(items.length, first + perView * 3); i += 1) {
      items[i].querySelectorAll('img[loading="lazy"]:not(.card__img--hover)').forEach((img) => {
        img.loading = 'eager';
      });
    }
  }

  async loadMore() {
    if (this.loading || !this.nextUrl) return;
    this.loading = true;
    const loader = this.track.querySelector('[data-carousel-loader]');
    if (loader) loader.hidden = false;

    try {
      const response = await fetch(this.nextUrl);
      if (!response.ok) throw new Error(response.statusText);
      const doc = parseHTML(await response.text());
      const end = this.track.querySelector('[data-carousel-end]');
      doc.querySelectorAll('[data-carousel-item]').forEach((item) => {
        this.track.insertBefore(document.adoptNode(item), end);
      });
      this.nextUrl = doc.querySelector('[data-next-url]')?.dataset.nextUrl || null;
    } catch {
      this.nextUrl = null;
    } finally {
      this.loading = false;
      if (loader) loader.hidden = !this.nextUrl;
      reveal(this.track);
      this.update();
    }
  }

  enableDrag() {
    this.track.addEventListener('dragstart', (event) => event.preventDefault());
    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      const startX = event.clientX;
      const startLeft = this.track.scrollLeft;
      let dragging = false;

      const onMove = (moveEvent) => {
        const dx = moveEvent.clientX - startX;
        if (!dragging && Math.abs(dx) > 6) {
          dragging = true;
          this.track.classList.add('is-dragging');
        }
        if (dragging) this.track.scrollLeft = startLeft - dx;
      };

      const onUp = () => {
        removeEventListener('pointermove', onMove);
        removeEventListener('pointerup', onUp);
        if (!dragging) return;
        this.track.classList.remove('is-dragging');
        // Swallow the click that follows a drag so links don't fire.
        const swallow = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        this.track.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => this.track.removeEventListener('click', swallow, { capture: true }), 60);
      };

      addEventListener('pointermove', onMove);
      addEventListener('pointerup', onUp);
    });
  }

  setupAutoplay() {
    const seconds = Number(this.dataset.autoplay);
    if (!seconds || reducedMotion.matches || window.theme.designMode) return;

    // Timed with requestAnimationFrame rather than setInterval, so pausing on
    // hover really pauses, and the progress bar (hero) always matches the
    // moment the slide changes.
    const duration = seconds * 1000;
    let inView = false;
    let paused = false;
    let last = 0;
    this.autoplayElapsed = 0;
    this.autoplayFrame = 0;
    this.toggleAttribute('data-autoplaying', true);

    const pause = () => (paused = true);
    const resume = () => (paused = false);
    this.addEventListener('pointerenter', pause);
    this.addEventListener('pointerleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
    this.track.addEventListener('touchstart', pause, { passive: true });

    const tick = (now) => {
      this.autoplayFrame = 0;
      if (!inView || document.hidden || !this.track.clientWidth) {
        last = 0;
        return; // restarted by the visibility observers below
      }
      if (last && !paused) this.autoplayElapsed += now - last;
      last = now;
      if (this.autoplayElapsed >= duration) {
        this.autoplayElapsed = 0;
        this.advance();
      }
      this.progressBar?.style.setProperty('--autoplay-progress', Math.min(1, this.autoplayElapsed / duration).toFixed(4));
      this.autoplayFrame = requestAnimationFrame(tick);
    };
    const start = () => {
      if (this.autoplayFrame) return;
      last = 0;
      this.autoplayFrame = requestAnimationFrame(tick);
    };

    this.visibility = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        if (inView) start();
      },
      { threshold: 0.4 }
    );
    this.visibility.observe(this);
    document.addEventListener('visibilitychange', (this.onVisibilityChange = () => !document.hidden && start()));
  }

  advance() {
    const atEnd = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 4;
    if (atEnd && !this.nextUrl) this.track.scrollTo({ left: 0, behavior: 'smooth' });
    else this.scrollBy(this.itemWidth);
  }
}
define('carousel-slider', CarouselSlider);

/* ---------------------------------------------------------------------------
 * Tabs (ARIA tabs pattern)
 * ------------------------------------------------------------------------ */

class TabGroup extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => {
        const keys = { ArrowRight: 1, ArrowLeft: -1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        this.select((index + keys[event.key] + this.tabs.length) % this.tabs.length, true);
      });
    });
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.tabs.indexOf(event.target.closest('[role="tab"]'));
      if (index >= 0) this.select(index);
    });
  }

  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (this.panels[i]) this.panels[i].hidden = !selected;
    });
    if (focus) this.tabs[index].focus();
    this.panels[index]?.querySelector('carousel-slider')?.activate?.();
  }
}
define('tab-group', TabGroup);

/* ---------------------------------------------------------------------------
 * Product recommendations — fetched when the section nears the viewport
 * ------------------------------------------------------------------------ */

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('[data-track]') || !this.dataset.url) return;
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      if (!response.ok) throw new Error(response.statusText);
      const fresh = parseHTML(await response.text()).querySelector('product-recommendations');
      if (!fresh?.querySelector('[data-track]')) throw new Error('empty');
      this.innerHTML = fresh.innerHTML;
      reveal(this);
    } catch {
      this.hidden = true;
    }
  }
}
define('product-recommendations', ProductRecommendations);

/* ---------------------------------------------------------------------------
 * Sticky add-to-cart bar — appears once the main button scrolls away
 * ------------------------------------------------------------------------ */

class StickyAtc extends HTMLElement {
  connectedCallback() {
    const target = document.getElementById(this.dataset.target);
    if (!target) return;
    this.inert = true;
    this.observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', show);
      this.inert = !show;
    });
    this.observer.observe(target);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}
define('sticky-atc', StickyAtc);

/* ---------------------------------------------------------------------------
 * Share button — native share sheet on phones, a menu everywhere else
 * ------------------------------------------------------------------------ */

class ShareButton extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    const { url, title } = this.dataset;

    this.querySelector('summary').addEventListener('click', (event) => {
      if (navigator.share && matchMedia('(hover: none)').matches) {
        event.preventDefault();
        navigator.share({ title, url }).catch(() => {});
      }
    });

    const copy = this.querySelector('[data-copy]');
    const copyLabel = this.querySelector('[data-copy-label]');
    const original = copyLabel?.textContent;
    copy?.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Clipboard can be blocked (e.g. insecure context); fall back to a prompt.
        window.prompt(original, url);
        return;
      }
      copyLabel.textContent = strings.copied;
      setTimeout(() => {
        copyLabel.textContent = original;
      }, 2000);
    });

    this.onOutside = (event) => {
      if (this.details.open && !this.contains(event.target)) this.details.open = false;
    };
    this.onKey = (event) => {
      if (event.key === 'Escape' && this.details.open) {
        this.details.open = false;
        this.querySelector('summary').focus();
      }
    };
    document.addEventListener('click', this.onOutside);
    document.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutside);
    document.removeEventListener('keydown', this.onKey);
  }
}
define('share-button', ShareButton);

/* ---------------------------------------------------------------------------
 * Delivery estimate — business days from Theme settings → Store promises
 * ------------------------------------------------------------------------ */

class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const { min, max } = window.theme.delivery;
    const template = this.dataset.template;
    const target = this.querySelector('[data-dates]');
    if (!template || !target) return;

    const addBusinessDays = (days) => {
      const date = new Date();
      let added = 0;
      while (added < days) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 0 && date.getDay() !== 6) added += 1;
      }
      return date;
    };

    const format = new Intl.DateTimeFormat(window.theme.locale, { month: 'short', day: 'numeric' });
    target.innerHTML = template
      .replace('{from}', format.format(addBusinessDays(min)))
      .replace('{to}', format.format(addBusinessDays(max)));
  }
}
define('delivery-estimate', DeliveryEstimate);

/* ---------------------------------------------------------------------------
 * Quick view — loads templates/product.quick-view.liquid into a modal
 * ------------------------------------------------------------------------ */

document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('[data-quick-view]');
  if (!trigger) return;
  const dialog = document.getElementById('QuickView');
  if (!dialog) return;
  event.preventDefault();

  const content = dialog.querySelector('[data-quick-view-content]');
  content.replaceChildren();
  content.setAttribute('aria-busy', 'true');
  openDialog(dialog);

  try {
    const url = new URL(trigger.dataset.quickView, location.origin);
    url.searchParams.set('view', 'quick-view');
    const response = await fetch(url);
    if (!response.ok) throw new Error(response.statusText);
    const doc = parseHTML(await response.text());
    content.replaceChildren(...doc.body.childNodes);
  } catch {
    location.assign(trigger.dataset.quickView);
  } finally {
    content.removeAttribute('aria-busy');
  }
});

/* ---------------------------------------------------------------------------
 * Product cards — swatch hover swaps the photo; view-transition naming
 * ------------------------------------------------------------------------ */

function previewSwatch(event) {
  const swatch = event.target.closest?.('.swatch[data-srcset]');
  if (!swatch) return;
  const image = swatch.closest('.card')?.querySelector('.card__img:not(.card__img--hover)');
  if (!image) return;
  image.srcset = swatch.dataset.srcset;
  swatch
    .closest('.swatches')
    .querySelectorAll('.swatch')
    .forEach((other) => other.classList.toggle('is-active', other === swatch));
}
document.addEventListener('pointerover', previewSwatch);
document.addEventListener('focusin', previewSwatch);

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-card-link], .swatch');
  if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
  const image = link.closest('.card')?.querySelector('.card__img:not(.card__img--hover)');
  if (!image) return;
  document.querySelectorAll('.card__img').forEach((other) => (other.style.viewTransitionName = ''));
  document.documentElement.classList.add('vt-card');
  image.style.viewTransitionName = 'product-media';
});

/* ---------------------------------------------------------------------------
 * Boot
 * ------------------------------------------------------------------------ */

reveal();
document.addEventListener('shopify:section:load', (event) => reveal(event.target));

// Pasted description images often lack alt text; mark them decorative so
// screen readers skip them instead of reading out file names.
document.querySelectorAll('.rte img:not([alt])').forEach((img) => {
  img.alt = '';
});

// Customer address forms: preselect the saved country.
document.querySelectorAll('select[data-default]').forEach((select) => {
  if (select.dataset.default) select.value = select.dataset.default;
});
window.theme.reveal = reveal;
window.theme.renderCartSections = renderCartSections;

/* ---------------------------------------------------------------------------
 * Currency display — shows approximate prices in the shopper's own currency.
 *
 * Shopify charges in the store currency (USD), so this is display only: every
 * [data-cents] amount is re-rendered at a live rate, and a note tells the
 * shopper what they will actually be charged. If the rate feed is unreachable
 * the prices simply stay in the store currency.
 * ------------------------------------------------------------------------ */

const currencyConfig = window.theme.currency ?? {};
const RATE_TTL = 12 * 60 * 60 * 1000; // refresh twice a day; FX feeds update daily
const RATE_KEY = 'zv:rates';
const PICK_KEY = 'zv:currency';
const RATE_SOURCES = [
  'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/',
  'https://open.er-api.com/v6/latest/',
];

// Countries whose shoppers should see their own currency by default.
const COUNTRY_CURRENCY = {
  PK: 'PKR', US: 'USD', GB: 'GBP', AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD',
  BH: 'BHD', OM: 'OMR', CA: 'CAD', AU: 'AUD', NZ: 'NZD', JP: 'JPY', SG: 'SGD',
  MY: 'MYR', HK: 'HKD', KR: 'KRW', IL: 'ILS', CH: 'CHF', NO: 'NOK', SE: 'SEK',
  DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON',
};
const EURO = ['AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK'];
EURO.forEach((code) => (COUNTRY_CURRENCY[code] = 'EUR'));

const storage = {
  get(key) {
    try {
      return JSON.parse(localStorage.getItem(key) ?? 'null');
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* private mode: fall back to session-only behaviour */
    }
  },
};

class CurrencyDisplay {
  constructor(config) {
    this.base = (config.base || 'USD').toUpperCase();
    this.allowed = (config.list || '')
      .split(',')
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean);
    this.allowed.unshift(this.base);
    this.enabled = Boolean(config.enabled) && this.allowed.length > 1;
    this.country = (config.country || '').toUpperCase();
    this.rates = null;
    this.current = this.base;
    this.formatters = new Map();
  }

  get preferred() {
    const saved = storage.get(PICK_KEY);
    if (saved && this.allowed.includes(saved)) return saved;
    const local = COUNTRY_CURRENCY[this.country];
    return local && this.allowed.includes(local) ? local : this.base;
  }

  async start() {
    if (!this.enabled) return;
    document.querySelectorAll('currency-picker').forEach((picker) => (picker.hidden = false));
    const target = this.preferred;
    if (target !== this.base) await this.setCurrency(target);
    else this.syncPickers();
    this.watch();
  }

  async loadRates() {
    const cached = storage.get(RATE_KEY);
    if (cached?.base === this.base && Date.now() - cached.at < RATE_TTL) {
      this.rates = cached.rates;
      return this.rates;
    }
    const base = this.base.toLowerCase();
    for (const source of RATE_SOURCES) {
      try {
        const url = source.includes('jsdelivr') ? `${source}${base}.json` : `${source}${this.base}`;
        const response = await fetch(url, {
          mode: 'cors',
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          signal: AbortSignal.timeout(6000),
        });
        if (!response.ok) continue;
        const data = await response.json();
        const table = data.rates ?? data[base];
        if (!table || typeof table !== 'object') continue;
        // Only plain positive numbers, keyed by a currency code.
        const clean = Object.entries(table).filter(
          ([code, rate]) => /^[A-Za-z]{3}$/.test(code) && Number.isFinite(Number(rate)) && Number(rate) > 0
        );
        if (!clean.length) continue;
        this.rates = Object.fromEntries(clean.map(([code, rate]) => [code.toUpperCase(), Number(rate)]));
        storage.set(RATE_KEY, { base: this.base, at: Date.now(), rates: this.rates });
        return this.rates;
      } catch {
        /* try the next source */
      }
    }
    return null;
  }

  format(cents, currency, withCode = false) {
    const rate = currency === this.base ? 1 : this.rates?.[currency];
    if (!rate) return null;
    const amount = (Number(cents) / 100) * rate;
    const key = withCode ? currency + ':code' : currency;
    let formatter = this.formatters.get(key);
    if (!formatter) {
      // Currencies without subunits in practice (PKR, JPY, KRW) read better whole.
      const whole = ['PKR', 'JPY', 'KRW', 'VND', 'IDR', 'CLP', 'ISK'].includes(currency);
      formatter = new Intl.NumberFormat(window.theme.locale || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: withCode ? 'code' : 'symbol',
        maximumFractionDigits: whole ? 0 : 2,
        minimumFractionDigits: whole ? 0 : 2,
      });
      this.formatters.set(key, formatter);
    }
    return formatter.format(amount);
  }

  apply(root = document) {
    const nodes = root.querySelectorAll?.('[data-cents]') ?? [];
    nodes.forEach((node) => {
      if (!node.dataset.moneyOriginal) node.dataset.moneyOriginal = node.textContent.trim();
      if (this.current === this.base) {
        node.textContent = node.dataset.moneyOriginal;
        return;
      }
      const withCode = node.hasAttribute('data-with-currency');
      const text = this.format(node.dataset.cents, this.current, withCode);
      if (text) node.textContent = text;
    });
    document.querySelectorAll('[data-currency-note]').forEach((note) => {
      note.hidden = this.current === this.base;
      const label = note.querySelector('[data-currency-note-code]');
      if (label) label.textContent = this.base;
    });
  }

  async setCurrency(currency) {
    if (currency !== this.base && !this.rates) await this.loadRates();
    if (currency !== this.base && !this.rates?.[currency]) currency = this.base;
    this.current = currency;
    storage.set(PICK_KEY, currency);
    document.documentElement.dataset.currency = currency;
    this.apply();
    this.syncPickers();
  }

  syncPickers() {
    document.querySelectorAll('[data-currency-select]').forEach((select) => {
      if (select.value !== this.current) select.value = this.current;
    });
  }

  // Cart drawer, load more, quick view and carousel pages all inject new
  // markup, so convert whatever arrives after the first paint.
  watch() {
    const observer = new MutationObserver((records) => {
      if (this.current === this.base) return;
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches('[data-cents]')) this.apply(node.parentNode ?? document);
          else if (node.querySelector('[data-cents]')) this.apply(node);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

const currency = new CurrencyDisplay(currencyConfig);

class CurrencyPicker extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-currency-select]');
    this.select?.addEventListener('change', () => currency.setCurrency(this.select.value));
  }
}
define('currency-picker', CurrencyPicker);

currency.start();
window.theme.currencyDisplay = currency;
